import {
  formatWorldCityLabel,
} from "../../store/slices/worldGeographySlice"
import { formatTimezoneLabel } from "../../store/slices/timezonesSlice"

function trimStr(v) {
  return String(v ?? "").trim()
}

/** Infer social channel from handle URL when API omits type id. */
export function inferSocialDescriptionFromHandle(handle) {
  const h = trimStr(handle).toLowerCase()
  if (!h) return null
  if (h.includes("facebook.com") || h.includes("fb.com")) return "facebook"
  if (h.includes("instagram.com")) return "instagram_graph"
  if (h.includes("tiktok.com")) return "tiktok"
  if (h.includes("twitter.com") || h.includes("x.com")) return "twitter"
  return null
}

export function snapshotEditFormState({
  name,
  websiteUrl,
  socialNetworkTypeId,
  socialHandle,
  countryId,
  cityId,
  timezoneId,
  brandId,
  subBrandId,
}) {
  return {
    name: trimStr(name),
    websiteUrl: trimStr(websiteUrl),
    socialNetworkTypeId: socialNetworkTypeId ?? null,
    socialHandle: trimStr(socialHandle),
    countryId: countryId != null && countryId !== "" ? String(countryId) : null,
    cityId: cityId != null && cityId !== "" ? String(cityId) : null,
    timezoneId: timezoneId != null && timezoneId !== "" ? String(timezoneId) : null,
    brandId: brandId != null && brandId !== "" ? String(brandId) : null,
    subBrandId: subBrandId != null && subBrandId !== "" ? String(subBrandId) : null,
  }
}

export function editFormHasChanges(current, baseline) {
  if (!baseline) return false
  const keys = [
    "name",
    "websiteUrl",
    "socialNetworkTypeId",
    "socialHandle",
    "countryId",
    "cityId",
    "timezoneId",
    "brandId",
    "subBrandId",
  ]
  return keys.some((key) => {
    const a = current?.[key] ?? null
    const b = baseline?.[key] ?? null
    if (
      key === "socialNetworkTypeId" ||
      key === "countryId" ||
      key === "cityId" ||
      key === "timezoneId" ||
      key === "brandId" ||
      key === "subBrandId"
    ) {
      return String(a ?? "") !== String(b ?? "")
    }
    return trimStr(a) !== trimStr(b)
  })
}

function brandLabel(item) {
  if (!item || typeof item !== "object") return trimStr(item)
  return trimStr(item.brand || item.name)
}

/** Client `subBrand` from GET clients/users/{userId}/organizations/{orgId}. */
export function getClientSubBrandFields(client) {
  const sb = client?.subBrand
  const topBrand = client?.brand && typeof client.brand === "object" ? client.brand : null
  if (sb == null || sb === "") {
    return {
      subBrandId: null,
      subBrandText: "",
      brandId: topBrand?.id ?? null,
      brandText: brandLabel(topBrand),
    }
  }
  if (typeof sb === "string") {
    return {
      subBrandId: null,
      subBrandText: trimStr(sb),
      brandId: topBrand?.id ?? null,
      brandText: brandLabel(topBrand),
    }
  }
  const nested = sb.brand && typeof sb.brand === "object" ? sb.brand : null
  return {
    subBrandId: sb.id ?? null,
    subBrandText: trimStr(sb.subBrand || sb.name),
    brandId: nested?.id ?? topBrand?.id ?? null,
    brandText: brandLabel(nested) || brandLabel(topBrand),
  }
}

export function pickBrandFromSearch(items, query) {
  const list = Array.isArray(items) ? items : []
  const want = trimStr(query).toLowerCase()
  if (!list.length) return null
  if (!want) return list[0]
  const labelOf = (item) => brandLabel(item).toLowerCase()
  return (
    list.find((item) => labelOf(item) === want) ||
    list.find((item) => labelOf(item).startsWith(want)) ||
    list.find((item) => {
      const label = labelOf(item)
      return label.includes(want) || want.includes(label)
    }) ||
    list[0]
  )
}

export function brandSearchQueryFromSubBrand(subBrandText) {
  const name = trimStr(subBrandText)
  if (!name) return ""
  const token = name.split(/\s+/)[0]
  return /^\d+$/.test(token) ? token : name
}

/** Map GET client row → CreateAccountModal edit form state. */
export function initAccountFormFromClient(client) {
  if (!client || typeof client !== "object") {
    return {
      name: "",
      websiteUrl: "",
      socialNetworkTypeId: null,
      socialHandle: "",
      countryId: null,
      countryText: "",
      cityId: null,
      cityText: "",
      timezoneId: null,
      timezoneText: "",
      subBrandId: null,
      subBrandText: "",
      brandId: null,
      brandText: "",
    }
  }

  const wc = client.worldCity
  const country = wc?.worldState?.worldCountry ?? wc?.worldCountry ?? null
  const tz = client.timeZone ?? client.timezone

  let socialNetworkTypeId =
    client.socialNetworkTypeId ?? client.socialNetworkType?.id ?? null
  let socialHandle = ""
  if (typeof client.socialHandle === "string") {
    socialHandle = trimStr(client.socialHandle)
  } else if (client.socialHandle && typeof client.socialHandle === "object") {
    socialHandle = trimStr(client.socialHandle.handle)
    socialNetworkTypeId =
      socialNetworkTypeId ?? client.socialHandle.socialNetworkTypeId ?? null
  }

  const timezoneId =
    tz != null && typeof tz === "object" && tz.id != null
      ? tz.id
      : client.timezoneId ?? null

  const brandFields = getClientSubBrandFields(client)

  return {
    name: trimStr(client.name ?? client.clientName),
    websiteUrl: trimStr(client.websiteUrl ?? client.website),
    socialNetworkTypeId: socialNetworkTypeId ?? null,
    socialHandle,
    countryId: country?.id ?? null,
    countryText: trimStr(country?.name),
    cityId: wc?.id ?? null,
    cityText: formatWorldCityLabel(wc) || trimStr(wc?.name),
    timezoneId,
    timezoneText: formatTimezoneLabel(tz),
    ...brandFields,
  }
}
